/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

/**
 * Known partner tier strings as they appear in Airtable. Open union — keeps
 * `string` as the fallback so unexpected values flow through `classifyTier`
 * rather than tripping a type error at the fetcher boundary.
 */
export type PartnerBadgeLevel = "Bronze" | "Silver" | "Gold" | "Academic" | (string & {});

/**
 * Normalized partner tier. Derived from `badgeLevel` via `classifyTier` so
 * cards never need to string-sniff badge labels themselves.
 */
export type PartnerTier = "bronze" | "silver" | "gold" | "no-badge" | "academic" | "unknown";

/**
 * Classify a raw Airtable `badgeLevel` string into one of the known tiers.
 * Case-insensitive, trims whitespace; anything containing "academic" maps to
 * the academic tier so multi-word labels ("Academic Partner") still resolve.
 *
 * Unrecognized or missing values return `fallbackTier`, which defaults to
 * `"no-badge"`; callers can override it (e.g. pass `"unknown"`) when they need
 * to distinguish "no tier" from "unclassified".
 */
export function classifyTier(
	badgeLevel: string | undefined,
	fallbackTier: PartnerTier = "no-badge" as const,
): PartnerTier {
	const v = badgeLevel?.toLowerCase().trim() ?? "";
	if (v.includes("academic")) return "academic";
	if (v === "bronze") return "bronze";
	if (v === "silver") return "silver";
	if (v === "gold") return "gold";
	if (v === "no-badge") return "no-badge";
	return fallbackTier;
}

/**
 * A Gold-certified instructor from the UAI Airtable base.
 *
 * Source: `appTXNGNPznnzwjC0` / `MASTER copy 3`, filtered to
 * `{2026 Badge}='GOLD'`.
 */
export interface Instructor {
	/**
	 * Stable identifier — Airtable's numeric `ID` field when present, falling
	 * back to the record's `recXXX` row id otherwise. String type accommodates
	 * both so React `key={card.id}` stays stable.
	 */
	id: string;
	name: string;
	country: string;
	industries: string[];
	geo: string;
	/**
	 * Raw `2026 Badge` value from Airtable. Always `"GOLD"` since the fetcher
	 * filters to that tier — typed as plain string so a future tier rollout
	 * (Silver/Bronze instructors) doesn't break the contract. Cards don't
	 * need to read it; the variant rendering is hard-coded to gold.
	 *
	 * Distinct from `PartnerBadgeLevel` (Bronze/Silver/Gold/Academic) on
	 * purpose — Instructor and Partner tier vocabularies are independent.
	 */
	badgeLevel: string;
	linkedInUrl?: string;
	edcUrl?: string;
	artStationUrl?: string;
	yearQualified?: string;
}

/**
 * A training partner from the UE training-partner Airtable base.
 *
 * Schema not yet finalized — fields here are the planned shape based on the
 * Figma design (logo, name, country, badge tier, delivery-mode tags). Wiring
 * lands when the partner page goes live; for now the fetcher returns `[]`.
 */
export interface TrainingPartner {
	id: string;
	name: string;
	country: string;
	industries: string[];
	logoUrl?: string;
	websiteUrl?: string;
	email?: string;
	/** Open union of known tiers. Use `classifyTier` to normalize to `PartnerTier`. */
	badgeLevel?: PartnerBadgeLevel;
	languages?: string[];
}

/**
 * Case study attached to a service partner — surfaced on the detail page only
 * (PRD: "Card w/ name, link, and image on detail page"). All parts optional;
 * an empty case study (every part blank) is dropped from the list rather than
 * carried as an empty object.
 */
export interface ServicePartnerCaseStudy {
	title?: string;
	details?: string;
	imageUrl?: string;
	tags?: string[];
}

/**
 * A service partner from the UE `New SP Brand Info` Airtable tab
 * (`appXeEl6DQgzqdZY1` / `tblxZviZ09SRJVHIt`), filtered to
 * `{Ready for Directory}=TRUE()`.
 *
 * Fields map 1:1 to Airtable columns (no normalization/combining) — see
 * `mapRecord` in `service-partners.ts` for the column-by-column source.
 * Multi-value columns are always arrays (defaulting to `[]`) so consumers
 * never null-check before mapping; scalar text/links are optional.
 *
 * There is no badge/tier column for service partners — the card renders a
 * neutral "Service partner" badge, so this shape has no `badgeLevel`.
 */
export interface ServicePartner {
	id: string;
	/** `Org Name` (formula `{Organization Name}&""`), falling back to `Partner Name` / `Name`. */
	name: string;
	/**
	 * URL-safe slug. Sourced from Airtable — the manual `Slug (Override)` column
	 * when set, otherwise the formula-driven `Slug (Auto-generated-fallback)`
	 * (falling back to `slugify(name)` only for rows predating those columns).
	 * Always re-run through `slugify` so it is path-safe. Forms the trailing path
	 * segment of the partner's detail-page URL and is the key the detail template
	 * matches the requested path against.
	 */
	slug: string;
	/** `Company Highlights`. */
	description?: string;
	/** First `Logo` attachment's `large` thumbnail. */
	logoUrl?: string;
	/** First entry of `Website Rollup (from Organization Name)`. */
	website?: string;
	/** `Contact Us for directory`. */
	email?: string;
	/** `Countries Supported` (PRD "HQ (Country)"). */
	countriesSupported: string[];
	/** `Office Locations`. */
	officeLocations: string[];
	/** `Languages Supported Rollup (from Organization Name)`. */
	languages: string[];
	/** `Industries Supported Rollup (from Organization Name)` (wrapping quotes stripped). */
	industries: string[];
	/** `UE Skills`. */
	ueSkills: string[];
	/** `Support Type` (PRD "Services Offered"). */
	supportType: string[];
	/**
	 * Up to two case studies, sourced from the indexed `Case Study 1 …` /
	 * `Case Study 2 …` Airtable columns. Empty slots are dropped, so this is
	 * `[]`, a single entry, or two — never holds a blank case study.
	 */
	caseStudies: ServicePartnerCaseStudy[];
	/**
	 * Absolute path to the partner's detail page (`<directory path>/<slug>`).
	 * Only populated when the fetcher is given the directory's URL path
	 * (`fetchAllServicePartners(basePath)`); `undefined` otherwise. The Airtable
	 * layer can't know the CMS page slug on its own, so the consuming directory
	 * template passes its own live path in rather than hardcoding a prefix here.
	 */
	learnMoreUrl?: string;
	badgeLevel?: string;
}
